import { VStack, Text, HStack } from "@chakra-ui/react";
import { UseFormWatch, UseFormSetValue, useFormContext } from "react-hook-form";
import { S3Response } from "react-s3-uploader";
import { AdCreativeFormData } from "../../forms/adcreative";
import { useCallback, useEffect, useRef, useState } from "react";
import CarouselSection from "./CarouselSection";
import Container from "../Container";
import { ErrorMessage } from "@hookform/error-message";
import { validateAspectRatio } from "src/utils/aspectRatioValidator";
import { validatePortraitAspectRatio } from "src/utils/aspectRatio";

interface CarouselTabProps {
  createPublicUploadUrl: (params: {
    file: File;
    callback: (params: S3Response) => void | Promise<void | string>;
  }) => Promise<S3Response & { cdn_url: string }>;
}

const CarouselTab = ({ createPublicUploadUrl }: CarouselTabProps) => {
  const {
    formState: { errors },
    setError,
    setValue,
    watch,
  } = useFormContext<AdCreativeFormData>();
  const images = watch("images");
  const videos = watch("videos");
  const carouselMediaType = watch("carousel_media_type");

  const [mediaOrder, setMediaOrder] = useState<number[]>([]);
  const mediaOrderRef = useRef<number[]>([]);

  // Combine both images and videos to show all slides
  const mediaItems = [...(images?.portrait || []), ...(videos?.portrait || [])];

  // Initialize order when media items change
  useEffect(() => {
    const imagesLength = images?.portrait?.length || 0;
    const videosLength = videos?.portrait?.length || 0;

    const order = Array.from(
      { length: imagesLength + videosLength },
      (_, i) => i
    );

    setMediaOrder(order);
  }, [images?.portrait?.length, videos?.portrait?.length]);

  useEffect(() => {
    mediaOrderRef.current = mediaOrder;
  }, [mediaOrder]);

  const addMedia = () => {
    // Add to both arrays - the media type will be determined when user uploads
    const currentImages = images?.portrait || [];
    const currentVideos = videos?.portrait || [];

    const totalItems = currentImages.length + currentVideos.length;

    if (totalItems < 10) {
      // Add empty placeholder to images array (will be moved to videos if video is uploaded)
      setValue(
        `images.portrait`,
        [...currentImages, { fileKey: null, picture_url: null }],
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        }
      );
    }
  };

  const removeMedia = (index: number) => {
    // Find which array the item is in and remove it
    const currentImages = images?.portrait || [];
    const currentVideos = videos?.portrait || [];

    // Check if the index corresponds to an image or video
    if (index < currentImages.length) {
      // It's an image
      const updatedImages = currentImages.filter((_, i) => i !== index);
      setValue(`images.portrait`, updatedImages, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    } else {
      // It's a video - adjust index for video array
      const videoIndex = index - currentImages.length;
      const updatedVideos = currentVideos.filter((_, i) => i !== videoIndex);
      setValue(`videos.portrait`, updatedVideos, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  };

  const updateMedia = async (index: number, result: any) => {
    const isVideo = result.mime_type.startsWith("video/");
    const currentImages = images?.portrait || [];
    const currentVideos = videos?.portrait || [];

    // Build the combined mediaItems array to work with
    const currentMediaItems = [...currentImages, ...currentVideos];

    if (isVideo) {
      const { isValid, errorMessage } = await validatePortraitAspectRatio(
        result.file,
        0.1
      );

      if (!isValid) {
        setError("videos.portrait", {
          message: errorMessage,
        });
        return;
      }

      setValue("carousel_media_type", "video");

      // Build the new video item
      const newVideoItem = {
        fileKey: result.file_key,
        source: result.cdn_url,
        thumbnail_url: result.cdn_url,
      };

      // Update the item at the given index in the combined array
      const updatedMediaItems = [...currentMediaItems];
      updatedMediaItems[index] = newVideoItem;

      // Use mediaOrder to maintain visual order when separating items
      const currentOrder =
        mediaOrderRef.current.length > 0
          ? mediaOrderRef.current
          : Array.from({ length: updatedMediaItems.length }, (_, i) => i);

      // Get items in the order defined by mediaOrder
      const orderedItems = currentOrder.map(
        (orderIndex) => updatedMediaItems[orderIndex]
      );

      // Separate back into images and videos arrays while maintaining order
      const updatedImages: typeof currentImages = [];
      const updatedVideos: typeof currentVideos = [];

      orderedItems.forEach((item) => {
        if (item && "source" in item && item.source) {
          // It's a video
          updatedVideos.push(item);
        } else if (item) {
          // It's an image (or placeholder)
          updatedImages.push(item);
        }
      });

      setValue(`images.portrait`, updatedImages, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setValue(`videos.portrait`, updatedVideos, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    } else {
      setValue("carousel_media_type", "photo");

      const { isValid, errorMessage } = await validatePortraitAspectRatio(
        result.file,
        0.1
      );

      if (!isValid) {
        setError("images.portrait", {
          message: errorMessage,
        });
        return;
      }

      // Build the new image item
      const newImageItem = {
        fileKey: result.file_key,
        picture_url: result.cdn_url,
      };

      // Update the item at the given index in the combined array
      const updatedMediaItems = [...currentMediaItems];
      updatedMediaItems[index] = newImageItem;

      // Use mediaOrder to maintain visual order when separating items
      const currentOrder =
        mediaOrderRef.current.length > 0
          ? mediaOrderRef.current
          : Array.from({ length: updatedMediaItems.length }, (_, i) => i);

      // Get items in the order defined by mediaOrder
      const orderedItems = currentOrder.map(
        (orderIndex) => updatedMediaItems[orderIndex]
      );

      // Separate back into images and videos arrays while maintaining order
      const updatedImages: typeof currentImages = [];
      const updatedVideos: typeof currentVideos = [];

      orderedItems.forEach((item) => {
        if (item && "source" in item && item.source) {
          // It's a video
          updatedVideos.push(item);
        } else if (item) {
          // It's an image (or placeholder)
          updatedImages.push(item);
        }
      });

      setValue(`images.portrait`, updatedImages, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setValue(`videos.portrait`, updatedVideos, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  };

  const onUpdateOrder = useCallback(() => {
    const newOrder = mediaOrderRef.current;
    if (!newOrder || newOrder.length === 0) return;

    const reorderedItems = newOrder.map((orderIndex) => mediaItems[orderIndex]);

    // Separate images and videos back into their respective arrays
    const reorderedImages = [];
    const reorderedVideos = [];

    reorderedItems.forEach((item) => {
      if (item && "thumbnail_url" in item && item.thumbnail_url) {
        // It's a video
        reorderedVideos.push(item);
      } else if (item) {
        // It's an image
        reorderedImages.push(item);
      }
    });

    setValue(`images.portrait`, reorderedImages, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`videos.portrait`, reorderedVideos, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [mediaItems, setValue]);

  const handleMouseDownReorder = useCallback(
    (e: React.PointerEvent, item: any, dc: any) => {
      dc.start(e);
      document.addEventListener(
        "mouseup",
        () => {
          onUpdateOrder();
        },
        { once: true }
      );
    },
    [onUpdateOrder]
  );

  return (
    <VStack spacing={4}>
      <Container>
        <CarouselSection
          format="portrait"
          label="Carousel"
          carouselMediaType={carouselMediaType}
          mediaItems={mediaItems}
          createPublicUploadUrl={createPublicUploadUrl}
          updateMedia={updateMedia}
          removeMedia={removeMedia}
          addMedia={addMedia}
          handleMouseDownReorder={handleMouseDownReorder}
          mediaOrder={mediaOrder}
          setMediaOrder={setMediaOrder}
        />
        <ErrorMessage
          name={`images.portrait`}
          errors={errors}
          render={({ message }) => (
            <Text pt={2} fontSize="sm" color="kydRed.200">
              {message}
            </Text>
          )}
        />
      </Container>
    </VStack>
  );
};

export default CarouselTab;
